/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, useRef, useEffect } from "react";
import PropTypes from "prop-types";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDDropzone from "components/MDDropzone";

function Media({ values, onChange }) {
  const dropzoneRef = useRef(null);

  // Inject file ke Dropzone saat mount jika dokumen tidak null
  useEffect(() => {
    if (
      dropzoneRef.current &&
      values.dokumen &&
      Array.isArray(values.dokumen) &&
      values.dokumen.length > 0
    ) {
      const dz = dropzoneRef.current.dropzone;
      // Hanya inject jika Dropzone kosong
      if (dz && dz.files.length === 0) {
        values.dokumen.forEach((file) => {
          // Hindari duplikasi file
          if (
            !dz.files.find((f) => f.name === file.name && f.size === file.size)
          ) {
            dz.emit("addedfile", file);
            dz.emit("complete", file);
          }
        });
      }
    }
  }, [values.dokumen]);

  // Handler: update dokumen di parent sesuai file di Dropzone
  const handleDropzoneChange = (files) => {
    if (typeof onChange === "function") {
      onChange({ dokumen: files });
    }
  };

  // Preview file yang sudah diupload (tabel)
  const renderPreview = () => {
    if (values.dokumen && values.dokumen.length > 0) {
      return (
        <div style={{ marginBottom: 16 }}>
          <MDTypography variant="button" fontWeight="bold" color="text" mb={1}>
            File terupload:
          </MDTypography>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              marginTop: 8,
              fontSize: 13,
              fontFamily: "inherit, Arial, sans-serif",
            }}
          >
            <thead>
              <tr style={{ background: "#f5f5f5" }}>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: 8,
                    textAlign: "center",
                    fontWeight: 700,
                    fontSize: 13,
                    fontFamily: "inherit, Arial, sans-serif",
                  }}
                >
                  No
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: 8,
                    textAlign: "left",
                    fontWeight: 700,
                    fontSize: 13,
                    fontFamily: "inherit, Arial, sans-serif",
                  }}
                >
                  Nama File
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: 8,
                    textAlign: "right",
                    fontWeight: 700,
                    fontSize: 13,
                    fontFamily: "inherit, Arial, sans-serif",
                  }}
                >
                  Ukuran
                </th>
              </tr>
            </thead>
            <tbody>
              {values.dokumen.map((file, idx) => (
                <tr key={idx}>
                  <td
                    style={{
                      border: "1px solid #ddd",
                      padding: 8,
                      textAlign: "center",
                      fontWeight: 400,
                      fontSize: 12,
                      fontFamily: "inherit, Arial, sans-serif",
                    }}
                  >
                    {idx + 1}
                  </td>
                  <td
                    style={{
                      border: "1px solid #ddd",
                      padding: 8,
                      fontWeight: 400,
                      fontSize: 12,
                      fontFamily: "inherit, Arial, sans-serif",
                    }}
                  >
                    {file.name}
                  </td>
                  <td
                    style={{
                      border: "1px solid #ddd",
                      padding: 8,
                      textAlign: "right",
                      fontWeight: 400,
                      fontSize: 12,
                      fontFamily: "inherit, Arial, sans-serif",
                    }}
                  >
                    {(file.size / 1024).toFixed(2)} KB
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return null;
  };

  return (
    <MDBox>
      <MDTypography variant="h5">Dokumen</MDTypography>
      <MDBox mt={3}>
        <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
          <MDTypography
            component="label"
            variant="button"
            fontWeight="regular"
            color="text"
          >
            Upload dokumen, jika ada (bisa lebih dari satu file)
          </MDTypography>
        </MDBox>
        {renderPreview()}
        {useMemo(
          () => (
            <MDDropzone
              options={{ addRemoveLinks: true, uploadMultiple: true }}
              onChange={handleDropzoneChange}
              ref={dropzoneRef}
            />
          ),
          []
        )}
      </MDBox>
    </MDBox>
  );
}

Media.propTypes = {
  values: PropTypes.object,
  onChange: PropTypes.func,
};

export default Media;
